import { useState } from 'react';
import type { Rate, Taper, Weekday } from '@tariff/authoring';
import { WEEKDAY_ORDER } from '@tariff/authoring';
import {
  allowPeriod, applyPeriod, excludePeriod, periodWindowFor, removePlacementWindow, type Period,
} from '@tariff/periods';
import { useT, type MessageKey, type TFunction } from '../lib/i18n';
import { money, toDay, toIso } from '../lib/format';

/**
 * What an offer IS, in sentences, editable.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 *
 * Every amount on a rate could be typed in and nothing else could. The shape of
 * the offer — how many nights it covers, when it may be used, which days it may
 * start on, whether it repeats and how the repeats get cheaper — was readable in
 * a table and changeable only in the JSON textarea below the panel, whose worst
 * failure message is "That is not valid JSON".
 *
 * That was defensible while the whole job was copying next season's prices off a
 * sheet. It stops being defensible the moment the park wants a *different offer*
 * — a two-week package that did not exist last year, a rate that only starts on
 * Saturdays in summer — because there is no version of that which is only a
 * number.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS NOT THE LEGACY FORMAT WITH LABELS ON IT
 * ---------------------------------------------------------------------------
 *
 * The legacy calculator spread one idea across six interacting scalars, and
 * whether they did anything depended on a relationship between two of them that
 * no field name mentioned. Screens built on formats like that ask the operator to
 * learn the format, and the operator's actual question is never "what is
 * `if_factor_includes_start`" — it is "does the third week get cheaper".
 *
 * So the controls here are the questions, and the storage is a detail:
 *
 *   "One booking of this covers [7] nights"
 *   "Guests may keep extending it, [7] nights at a time, at most [2] times"
 *   "Each night after [14] costs [95]% of the night before, never under [EUR 25]"
 *
 * The compounding tail and the whole-run discount are the two things the legacy
 * pieces could express that a price list cannot, and they are here as two named
 * choices rather than as a factor and four flags.
 *
 * ---------------------------------------------------------------------------
 * WHY A SENTENCE AT THE TOP
 * ---------------------------------------------------------------------------
 *
 * Structure is where a wrong edit is SILENT: a mistyped price shows up as the
 * wrong number on the screen, a mistyped window shows up as a stay that quietly
 * prices from a different rate in October. `Summary` reads the whole rate back
 * as one sentence, so the operator checks what they made against what they
 * meant, rather than against the boxes they just filled in.
 */

// ---------------------------------------------------------------------------
// Dates: the operator's last night, the model's exclusive bound
// ---------------------------------------------------------------------------

/**
 * `active.until` is exclusive — the first night NOT covered. An operator asked
 * for "the last night this applies to" and typing the model's bound gets it
 * wrong by one every time, in the direction that silently extends a season.
 * Converted here, in one place, rather than explained in a hint nobody reads.
 */
const lastNight = (untilIso: string): string => {
  const d = toDay(untilIso);
  return d == null ? untilIso : toIso(d - 1);
};

const untilFromLastNight = (lastIso: string): string => {
  const d = toDay(lastIso);
  return d == null ? lastIso : toIso(d + 1);
};

// ---------------------------------------------------------------------------
// Reading a rate back as a sentence
// ---------------------------------------------------------------------------

/** Weekday names in the reader's language, joined the way a sentence joins. */
function dayList(t: TFunction, days: readonly Weekday[]): string {
  return days.map((d) => t(`day.long.${d}` as MessageKey)).join(', ');
}

export function summarise(t: TFunction, r: Rate): string {
  const parts: string[] = [];
  // `count` picks the plural form, `nights` fills the sentence. Passing only
  // `nights` chose "other" for a one-night rate, which is how "Covers 1 nights"
  // reaches a screen.
  parts.push(t('shape.sumCovers', { count: r.nights, nights: r.nights }));
  if (r.minNights != null) parts.push(t('shape.sumTrimmed', { nights: r.minNights }));
  parts.push(t('shape.sumWindow', { from: r.active.from, until: lastNight(r.active.until) }));
  if (r.arriveOn?.length) parts.push(t('shape.sumStartsOn', { days: dayList(t, r.arriveOn) }));
  if (r.stayArriveOn?.length) {
    parts.push(t('shape.sumStayStartsOn', { days: dayList(t, r.stayArriveOn) }));
  }
  if (r.extend) {
    parts.push(r.extend.maxSteps != null
      ? t('shape.sumExtendsCapped', {
        nights: r.extend.stepNights, times: r.extend.maxSteps,
      })
      : t('shape.sumExtends', { nights: r.extend.stepNights }));
    const taper = r.extend.taper;
    if (taper && taper.mode !== 'none') {
      parts.push(taper.mode === 'perNight'
        ? t('shape.sumTaperPerNight', {
          pct: Math.round(taper.factor * 100), nights: taper.afterNights,
        })
        : t('shape.sumTaperWholeStay', {
          pct: Math.round(taper.factor * 100), nights: taper.afterNights,
        }));
      if (taper.floorPerNight != null) {
        parts.push(t('shape.sumFloor', { price: money(taper.floorPerNight) }));
      }
    }
  }
  if (r.stayNights?.min != null || r.stayNights?.max != null) {
    parts.push(t('shape.sumStayLength', {
      min: r.stayNights.min ?? 1, max: r.stayNights.max ?? '∞',
    }));
  }
  if (r.availability === 'closed') parts.push(t('shape.sumBlocks'));
  else if (r.availability === 'refuses') parts.push(t('shape.sumRefuses'));
  if (r.needsStaffConfirmation) parts.push(t('shape.sumNeedsStaff'));
  return `${parts.join('. ')}.`;
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

/**
 * A whole number, held as text while it is being typed.
 *
 * Same reason as the money boxes: bind the input to the parsed value and a field
 * cannot be cleared to retype it, because the empty string parses to nothing and
 * renders back as the old number. Clearing a box is how people retype.
 */
function Num({ id, label, value, min, disabled, placeholder, onSet, hint, decimals }: {
  id: string;
  label: string;
  value: number | null | undefined;
  min?: number;
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
  /**
   * Decimal places to keep, for the fields where whole numbers are the wrong
   * answer.
   *
   * A taper percentage is the case: rounding it to whole percent moves a
   * long-stay price by tens of euros, and 99% is not even expressible as
   * "a bit under 1% a night" — the real figures are like 98.9838. Every other
   * field here counts nights, and half a night is not a thing.
   */
  decimals?: number;
  onSet: (v: number | null) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    // The hint sits OUTSIDE the label element on purpose. Inside it, the hint
    // becomes part of the field's accessible name — "At most (steps) Leave empty
    // for no limit." — which is what a screen reader announces and what anybody
    // looking the field up by its name fails to find.
    <div className="shape-field">
      <label className="shape-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        className="amount"
        disabled={disabled}
        placeholder={placeholder}
        value={text ?? (value == null ? '' : show(value, decimals))}
        onFocus={() => setText(value == null ? '' : show(value, decimals))}
        onChange={(e) => {
          setText(e.target.value);
          const raw = e.target.value.trim();
          if (raw === '') { onSet(null); return; }
          const n = Number(raw.replace(',', '.'));
          if (!Number.isFinite(n)) return;
          if (min != null && n < min) return;
          onSet(decimals ? n : Math.round(n));
        }}
        onBlur={() => setText(null)}
      />
      {hint && <span className="shape-hint">{hint}</span>}
    </div>
  );
}

function DateBox({ id, label, value, disabled, onSet }: {
  id: string; label: string; value: string; disabled?: boolean;
  onSet: (iso: string) => void;
}) {
  return (
    <div className="shape-field">
      <label className="shape-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="date"
        disabled={disabled}
        value={value}
        onChange={(e) => { if (e.target.value) onSet(e.target.value); }}
      />
    </div>
  );
}

function Check({ id, label, checked, disabled, onSet, hint }: {
  id: string; label: string; checked: boolean; disabled?: boolean; hint?: string;
  onSet: (v: boolean) => void;
}) {
  return (
    <div className="shape-check">
      <label htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onSet(e.target.checked)}
        />
        {label}
      </label>
      {/* Outside the label, like the field hints: a hint that joins the
          accessible name makes the control unfindable by the name it shows. */}
      {hint && <p className="shape-hint">{hint}</p>}
    </div>
  );
}

/**
 * Seven buttons, on or off.
 *
 * Weekdays are stored as identifiers (`saturday`) and shown as words in the
 * reader's language. NONE selected means "any day", which is the model's meaning
 * for an absent list and has to be said out loud — an empty set of buttons reads
 * as "no days allowed", which is the opposite.
 */
function Days({ label, value, disabled, hint, onSet }: {
  label: string;
  value: Weekday[] | undefined;
  disabled?: boolean;
  hint: string;
  onSet: (v: Weekday[] | undefined) => void;
}) {
  const t = useT();
  const on = new Set(value ?? []);
  return (
    <div className="shape-days">
      <span className="shape-label">{label}</span>
      <div className="row">
        {WEEKDAY_ORDER.map((d) => (
          <button
            type="button"
            key={d}
            className={`btn day${on.has(d) ? ' on' : ''}`}
            disabled={disabled}
            aria-pressed={on.has(d)}
            onClick={() => {
              const next = new Set(on);
              if (next.has(d)) next.delete(d); else next.add(d);
              const kept = WEEKDAY_ORDER.filter((x) => next.has(x));
              onSet(kept.length === 0 || kept.length === 7 ? undefined : kept);
            }}
          >
            {t(`day.short.${d}` as MessageKey)}
          </button>
        ))}
      </div>
      <p className="shape-hint">{on.size === 0 ? t('shape.anyDay') : hint}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

/**
 * "It should be down to EUR 45 by night fifty" — worked backwards into a rate.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PERCENTAGE IS THE WRONG THING TO ASK FOR
 * ---------------------------------------------------------------------------
 *
 * A park decides long stays in terms of the DESTINATION: a seasonal pitch works
 * out at about EUR 45 a night, a month should land near EUR 50. Nobody decides
 * "each night costs 98.9838% of the one before" — that is the arithmetic that
 * happens to produce the destination, and it is not a number anybody can
 * estimate, sanity-check or negotiate.
 *
 * So this asks for the destination and solves for the rate:
 *
 *   step × factor^e = target,  so  factor = (target / step) ^ (1 / e)
 *
 * where `e` is how many times the engine has applied the factor by the night in
 * question. That exponent is NOT `night − afterNights`, and assuming it was
 * produced EUR 45.54 where EUR 45 was asked for: the base block covers the first
 * `rate.nights`, extension steps cover `stepNights` each, and the factor first
 * applies to the step AFTER the threshold — so the step holding night N is
 * `ceil((N − nights) / stepNights) − 1` and the exponent is one more than its
 * distance past `afterNights`. Worked out here from the engine's own loop rather
 * than from the field names, and pinned by a test that prices a real stay.
 *
 * The result is written into the same field the operator can still edit by hand,
 * rather than stored as a second source of truth. There is one taper on the rate;
 * this is a calculator for it.
 *
 * ---------------------------------------------------------------------------
 * NOT THE SAME AS "CONVERGES TO"
 * ---------------------------------------------------------------------------
 *
 * `floorPerNight` is a CLAMP: the price stops falling when it reaches it, however
 * fast it was falling. This is a TARGET: it decides how fast. Set both to the
 * same number and the curve arrives exactly as it flattens, which is usually
 * what somebody means — but they are different statements and neither implies
 * the other, so neither field writes the other one.
 */
function TaperSolver({ rate, taper, onFactor }: {
  rate: Rate;
  taper: Extract<Taper, { mode: 'perNight' | 'wholeStay' }>;
  onFactor: (factor: number) => void;
}) {
  const t = useT();
  const [target, setTarget] = useState('');
  const [byStep, setByStep] = useState('');

  // What one step costs before any taper — the price the curve starts from.
  const stepPrice = rate.extend?.stepPrice?.base ?? rate.price?.base ?? 0;
  const stepNights = rate.extend?.stepNights ?? 1;
  const to = Number(target.replace(',', '.'));
  const at = Number(byStep.replace(',', '.'));
  // Which step holds that night, and how many times the factor has been applied
  // by then. See the note above: this is the engine's indexing, not the labels'.
  const stepIndex = Math.ceil((at - rate.nights) / stepNights) - 1;
  const exponent = stepIndex - taper.afterNights + 1;

  const solvable = Number.isFinite(to) && to > 0
    && Number.isFinite(at) && exponent >= 1 && stepPrice > 0 && to < stepPrice;

  return (
    <div className="shape-solver">
      <span className="shape-label">{t('shape.solverLegend')}</span>
      <div className="row">
        <label className="shape-field" htmlFor={`shape-${rate.id}-solve-to`}>
          <span className="shape-label">{t('shape.solverTarget')}</span>
          <input
            id={`shape-${rate.id}-solve-to`}
            type="text"
            inputMode="decimal"
            className="amount"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>
        <label className="shape-field" htmlFor={`shape-${rate.id}-solve-at`}>
          <span className="shape-label">{t('shape.solverByStep')}</span>
          <input
            id={`shape-${rate.id}-solve-at`}
            type="text"
            inputMode="numeric"
            className="amount"
            value={byStep}
            onChange={(e) => setByStep(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn"
          disabled={!solvable}
          onClick={() => onFactor((to / stepPrice) ** (1 / exponent))}
        >
          {t('shape.solverApply')}
        </button>
      </div>
      <p className="shape-hint">
        {stepPrice > 0
          ? t('shape.solverHint', {
            price: money(stepPrice),
            nights: stepNights,
          })
          : t('shape.solverNoStepPrice')}
      </p>
      {/* Why the button is off. A disabled control with no reason beside it is
          a dead end, and the two ways to get here are both easy to hit: asking
          for a night the taper has not reached yet — everything up to
          `nights + afterNights × stepNights` is still at full price — or asking
          it to go UP, which is not what a taper does. */}
      {!solvable && (target !== '' || byStep !== '') && (
        <p className="shape-hint">
          {to >= stepPrice && Number.isFinite(to)
            ? t('shape.solverNotDownwards', { price: money(stepPrice) })
            : t('shape.solverTooEarly', {
              night: rate.nights + Math.max(0, taper.afterNights) * stepNights + 1,
            })}
        </p>
      )}
    </div>
  );
}

/**
 * A number as an operator would write it: no trailing zeros, no scientific
 * notation, and no more decimals than were asked for.
 *
 * `98.98380000000001` is what the arithmetic produces and not what anybody typed.
 */
function show(value: number, decimals?: number): string {
  if (!decimals) return String(value);
  return String(Number(value.toFixed(decimals)));
}

/** Drop a key rather than store an empty value; absent and empty differ. */
function without<T extends object>(o: T, key: keyof T): T {
  const next = { ...o };
  delete next[key];
  return next;
}

export function RateShape({ rate, readOnly, periods = [], onChange }: {
  rate: Rate;
  readOnly?: boolean;
  /** The season's library, for filling windows from it. Empty is fine. */
  periods?: readonly Period[];
  onChange?: (next: Rate) => void;
}) {
  const t = useT();
  const disabled = !!readOnly || typeof onChange !== 'function';
  const set = (next: Rate) => onChange?.(next);
  const taper: Taper = rate.extend?.taper ?? { mode: 'none' };

  const setTaper = (next: Taper) => {
    if (!rate.extend) return;
    set({ ...rate, extend: { ...rate.extend, taper: next } });
  };

  /** Switching taper mode keeps the numbers, so a mis-click is undoable. */
  const changeTaperMode = (mode: Taper['mode']) => {
    if (mode === 'none') { setTaper({ mode: 'none' }); return; }
    const afterNights = taper.mode === 'none' ? rate.nights : taper.afterNights;
    const factor = taper.mode === 'none' ? 0.95 : taper.factor;
    const floor = taper.mode === 'none' ? undefined : taper.floorPerNight;
    setTaper(floor == null
      ? { mode, afterNights, factor }
      : { mode, afterNights, factor, floorPerNight: floor });
  };

  return (
    <div className="shape">
      <p className="shape-summary">{summarise(t, rate)}</p>

      <fieldset className="shape-set">
        <legend>{t('shape.legendCovers')}</legend>
        <Num
          id={`shape-${rate.id}-nights`}
          label={t('shape.nights')}
          value={rate.nights}
          min={1}
          disabled={disabled}
          onSet={(v) => set({ ...rate, nights: v ?? 1 })}
        />
        <Check
          id={`shape-${rate.id}-trim`}
          label={t('shape.trimmable')}
          hint={t('shape.trimmableHint')}
          checked={rate.minNights != null}
          disabled={disabled}
          onSet={(v) => set(v
            ? { ...rate, minNights: Math.max(1, rate.nights - 1) }
            : without(rate, 'minNights'))}
        />
        {rate.minNights != null && (
          <Num
            id={`shape-${rate.id}-min-nights`}
            label={t('shape.trimmedTo')}
            value={rate.minNights}
            min={1}
            disabled={disabled}
            onSet={(v) => set(v == null ? without(rate, 'minNights') : { ...rate, minNights: v })}
          />
        )}
      </fieldset>

      <fieldset className="shape-set">
        <legend>{t('shape.legendWhen')}</legend>
        <DateBox
          id={`shape-${rate.id}-from`}
          label={t('shape.firstNight')}
          value={rate.active.from}
          disabled={disabled}
          onSet={(iso) => set({ ...rate, active: { ...rate.active, from: iso } })}
        />
        <DateBox
          id={`shape-${rate.id}-until`}
          label={t('shape.lastNight')}
          value={lastNight(rate.active.until)}
          disabled={disabled}
          onSet={(iso) => set({
            ...rate, active: { ...rate.active, until: untilFromLastNight(iso) },
          })}
        />
        <p className="shape-hint">{t('shape.lastNightHint')}</p>
        <PeriodPicker
          rate={rate}
          periods={periods}
          disabled={disabled}
          onChange={set}
        />
      </fieldset>

      <fieldset className="shape-set">
        <legend>{t('shape.legendDays')}</legend>
        <Days
          label={t('shape.arriveOn')}
          value={rate.arriveOn}
          disabled={disabled}
          hint={t('shape.arriveOnHint')}
          onSet={(v) => set(v ? { ...rate, arriveOn: v } : without(rate, 'arriveOn'))}
        />
        <Days
          label={t('shape.departOn')}
          value={rate.departOn}
          disabled={disabled}
          hint={t('shape.departOnHint')}
          onSet={(v) => set(v ? { ...rate, departOn: v } : without(rate, 'departOn'))}
        />
        <Days
          label={t('shape.stayArriveOn')}
          value={rate.stayArriveOn}
          disabled={disabled}
          hint={t('shape.stayArriveOnHint')}
          onSet={(v) => set(v ? { ...rate, stayArriveOn: v } : without(rate, 'stayArriveOn'))}
        />
      </fieldset>

      <fieldset className="shape-set">
        <legend>{t('shape.legendExtend')}</legend>
        <Check
          id={`shape-${rate.id}-extends`}
          label={t('shape.extends')}
          hint={t('shape.extendsHint')}
          checked={!!rate.extend}
          disabled={disabled}
          onSet={(v) => set(v
            ? {
              ...rate,
              extend: {
                stepNights: rate.nights,
                // The step starts at the block's own price, which is the
                // answer for "another week at the same rate" and the thing an
                // operator would otherwise have to retype.
                stepPrice: { ...(rate.price ?? {}) },
                taper: { mode: 'none' },
              },
            }
            : without(rate, 'extend'))}
        />
        {rate.extend && (
          <>
            <Num
              id={`shape-${rate.id}-step-nights`}
              label={t('shape.stepNights')}
              value={rate.extend.stepNights}
              min={1}
              disabled={disabled}
              onSet={(v) => set({
                ...rate, extend: { ...rate.extend!, stepNights: v ?? 1 },
              })}
            />
            <Num
              id={`shape-${rate.id}-max-steps`}
              label={t('shape.maxSteps')}
              value={rate.extend.maxSteps}
              min={1}
              disabled={disabled}
              placeholder={t('shape.noLimit')}
              hint={t('shape.maxStepsHint')}
              onSet={(v) => set({
                ...rate,
                extend: v == null
                  ? without(rate.extend!, 'maxSteps')
                  : { ...rate.extend!, maxSteps: v },
              })}
            />
            <Check
              id={`shape-${rate.id}-resume`}
              label={t('shape.resumeAfterGap')}
              hint={t('shape.resumeAfterGapHint')}
              checked={!!rate.extend.resumeAfterGap}
              disabled={disabled}
              onSet={(v) => set({
                ...rate,
                extend: v
                  ? { ...rate.extend!, resumeAfterGap: true }
                  : without(rate.extend!, 'resumeAfterGap'),
              })}
            />

            <div className="shape-choice" role="radiogroup" aria-label={t('shape.taperLegend')}>
              <span className="shape-label">{t('shape.taperLegend')}</span>
              {(['none', 'perNight', 'wholeStay'] as const).map((mode) => (
                <label key={mode} htmlFor={`shape-${rate.id}-taper-${mode}`}>
                  <input
                    id={`shape-${rate.id}-taper-${mode}`}
                    type="radio"
                    name={`shape-${rate.id}-taper`}
                    checked={taper.mode === mode}
                    disabled={disabled}
                    onChange={() => changeTaperMode(mode)}
                  />
                  {t(`shape.taperMode.${mode}` as MessageKey)}
                </label>
              ))}
            </div>
            {taper.mode !== 'none' && (
              <>
                <Num
                  id={`shape-${rate.id}-taper-after`}
                  label={t('shape.taperAfter')}
                  value={taper.afterNights}
                  min={0}
                  disabled={disabled}
                  onSet={(v) => setTaper({ ...taper, afterNights: v ?? 0 })}
                />
                {/* Stored as a factor, typed as a percentage. Nobody prices a
                    third week at "zero point nine five".

                    Four decimals, not none. It was rounded to whole percent,
                    which is far too coarse to be the thing an operator sets: the
                    step from 99% to 98% is, over fifty nights, the difference
                    between converging on EUR 45 and on EUR 27. Worse, it was
                    round-tripped — opening a rate at 98.9838 and touching
                    anything else rewrote it as 99. */}
                <Num
                  id={`shape-${rate.id}-taper-pct`}
                  label={t('shape.taperPercent')}
                  value={taper.factor * 100}
                  decimals={4}
                  min={1}
                  disabled={disabled}
                  hint={t('shape.taperPercentHint')}
                  onSet={(v) => setTaper({ ...taper, factor: (v ?? 100) / 100 })}
                />
                {!disabled && (
                  <TaperSolver
                    rate={rate}
                    taper={taper}
                    onFactor={(factor) => setTaper({ ...taper, factor })}
                  />
                )}
              </>
            )}
          </>
        )}
      </fieldset>

      <fieldset className="shape-set">
        <legend>{t('shape.legendWhoMayUse')}</legend>
        <Num
          id={`shape-${rate.id}-stay-min`}
          label={t('shape.stayMin')}
          value={rate.stayNights?.min}
          min={1}
          disabled={disabled}
          placeholder={t('shape.noLimit')}
          onSet={(v) => {
            const stayNights = { ...(rate.stayNights ?? {}) };
            if (v == null) delete stayNights.min; else stayNights.min = v;
            set(Object.keys(stayNights).length === 0
              ? without(rate, 'stayNights') : { ...rate, stayNights });
          }}
        />
        <Num
          id={`shape-${rate.id}-stay-max`}
          label={t('shape.stayMax')}
          value={rate.stayNights?.max}
          min={1}
          disabled={disabled}
          placeholder={t('shape.noLimit')}
          onSet={(v) => {
            const stayNights = { ...(rate.stayNights ?? {}) };
            if (v == null) delete stayNights.max; else stayNights.max = v;
            set(Object.keys(stayNights).length === 0
              ? without(rate, 'stayNights') : { ...rate, stayNights });
          }}
        />
        <Num
          id={`shape-${rate.id}-max-placements`}
          label={t('shape.maxPlacements')}
          value={rate.maxPlacements}
          min={1}
          disabled={disabled}
          placeholder={t('shape.noLimit')}
          hint={t('shape.maxPlacementsHint')}
          onSet={(v) => set(v == null
            ? without(rate, 'maxPlacements') : { ...rate, maxPlacements: v })}
        />
      </fieldset>

      <fieldset className="shape-set">
        <legend>{t('shape.legendEffect')}</legend>
        <div className="shape-choice" role="radiogroup" aria-label={t('shape.legendEffect')}>
          {(['open', 'refuses', 'closed'] as const).map((a) => (
            <label key={a} htmlFor={`shape-${rate.id}-avail-${a}`}>
              <input
                id={`shape-${rate.id}-avail-${a}`}
                type="radio"
                name={`shape-${rate.id}-avail`}
                checked={(rate.availability ?? 'open') === a}
                disabled={disabled}
                onChange={() => set(a === 'open'
                  ? without(rate, 'availability') : { ...rate, availability: a })}
              />
              {t(`shape.availability.${a}` as MessageKey)}
            </label>
          ))}
        </div>
        {/* Two independent facts, not one scale: a rate can want a human to
            confirm it without blocking anything, and 21 of the park's do. */}
        <Check
          id={`shape-${rate.id}-staff`}
          label={t('shape.needsStaff')}
          hint={t('shape.needsStaffHint')}
          checked={!!rate.needsStaffConfirmation}
          disabled={disabled}
          onSet={(v) => set(v
            ? { ...rate, needsStaffConfirmation: true }
            : without(rate, 'needsStaffConfirmation'))}
        />
        <div className="shape-field">
          <label className="shape-label" htmlFor={`shape-${rate.id}-message`}>
            {t('shape.message')}
          </label>
          <input
            id={`shape-${rate.id}-message`}
            type="text"
            disabled={disabled}
            value={rate.message ?? ''}
            onChange={(e) => set(e.target.value
              ? { ...rate, message: e.target.value } : without(rate, 'message'))}
          />
        </div>
      </fieldset>
    </div>
  );
}

/**
 * The base window, the periods it is allowed in, and the periods it is kept out
 * of — all three from the library instead of typed twice.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ONE LIST AND NOT THREE
 * ---------------------------------------------------------------------------
 *
 * The operator's description was a base period that has to match first, then an
 * included list or an excluded one. Two of those already existed: the base is
 * `active`, and the included list is `placementWindows`, which is QPC's
 * `allowed_periods` under a clearer name and which the engine has enforced all
 * along. Only the way to say them was missing.
 *
 * Exclusion is not a third thing to store. "Low season except Ascension" and
 * "low season up to the 13th, and from the 18th" are the same statement, and
 * the engine can only be told the second. So the exclusion happens when the
 * button is pressed and the WINDOWS are what is kept. What the operator reads
 * back afterwards is what prices the stay — rather than an intent that gets
 * recompiled later, possibly into something else.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN EXCLUSION ACTUALLY DOES, SAID OUT LOUD
 * ---------------------------------------------------------------------------
 *
 * A stay must fit WHOLLY inside one window. So excluding Ascension from the
 * week rate does not make the holiday nights dearer — it stops the week rate
 * covering any stay that spans them, and the guest gets the next cheapest
 * tiling instead. That is usually what "this rate is not for Ascension weeks"
 * means, and it is emphatically not what "those nights cost more" means. The
 * hint says so, because the difference shows up in a price and not in the UI.
 */
function PeriodPicker({ rate, periods, disabled, onChange }: {
  rate: Rate;
  periods: readonly Period[];
  disabled: boolean;
  onChange: (next: Rate) => void;
}) {
  const t = useT();
  const [refused, setRefused] = useState<MessageKey | null>(null);

  // Sorted by date. A library is entered in whatever order somebody thought of
  // them; a list to pick from is read in calendar order.
  const library = [...periods].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  const byId = new Map(library.map((p) => [p.id, p]));
  const windows = rate.placementWindows ?? [];
  const from = rate.derivedFrom ? byId.get(rate.derivedFrom) : undefined;

  /** A period's option text, showing the window this rate would actually take. */
  const optionLabel = (p: Period) => {
    const w = periodWindowFor(rate, p);
    return `${p.label} · ${w.from} → ${lastNight(w.until)}`;
  };

  const act = (next: Rate | null, why: MessageKey) => {
    if (next === null) { setRefused(why); return; }
    setRefused(null);
    onChange(next);
  };

  if (library.length === 0) {
    return <p className="shape-hint">{t('shape.noPeriods')}</p>;
  }

  return (
    <>
      <label className="shape-field">
        {t('shape.fillFromPeriod')}
        <select
          value=""
          disabled={disabled}
          onChange={(e) => {
            const p = byId.get(e.target.value);
            if (p) act(applyPeriod(rate, p), 'shape.periodOutside');
          }}
        >
          <option value="">{t('shape.choosePeriod')}</option>
          {library.map((p) => (
            <option key={p.id} value={p.id}>{optionLabel(p)}</option>
          ))}
        </select>
      </label>
      {/* Which of a period's two windows was taken, said before it is taken.
          An operator who picks "Hemelvaart" and lands on 4 – 22 May has been
          handed a bug to report; one who reads it in the option has been told
          how arrangements work. */}
      <p className="shape-hint">
        {rate.role !== 'nightly' && rate.nights > 1
          ? t('shape.takesAppliesWindow')
          : t('shape.takesHolidayWindow')}
      </p>
      {from && <p className="shape-hint">{t('shape.copiedFrom', { label: from.label })}</p>}

      <div className="shape-windows">
        <span className="eyebrow">{t('shape.allowedIn')}</span>
        {windows.length === 0 ? (
          <p className="shape-hint">{t('shape.allowedAnywhere')}</p>
        ) : (
          <ul className="chips">
            {windows.map((w, i) => (
              <li key={`${w.from}-${w.until}`}>
                <span>{w.from} → {lastNight(w.until)}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="link"
                    aria-label={t('shape.removeWindow', { from: w.from })}
                    onClick={() => onChange(removePlacementWindow(rate, i))}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <label className="shape-field">
          {t('shape.allowPeriod')}
          <select
            value=""
            disabled={disabled}
            onChange={(e) => {
              const p = byId.get(e.target.value);
              if (p) act(allowPeriod(rate, p), 'shape.allowOutside');
            }}
          >
            <option value="">{t('shape.choosePeriod')}</option>
            {library.map((p) => (
              <option key={p.id} value={p.id}>{optionLabel(p)}</option>
            ))}
          </select>
        </label>
        <label className="shape-field">
          {t('shape.excludePeriod')}
          <select
            value=""
            disabled={disabled}
            onChange={(e) => {
              const p = byId.get(e.target.value);
              if (p) act(excludePeriod(rate, p), 'shape.excludeEverything');
            }}
          >
            <option value="">{t('shape.choosePeriod')}</option>
            {library.map((p) => (
              // The holiday window, always — see `excludePeriod`.
              <option key={p.id} value={p.id}>
                {`${p.label} · ${p.from} → ${lastNight(p.until)}`}
              </option>
            ))}
          </select>
        </label>
        {windows.length > 0 && <p className="shape-hint">{t('shape.wholeStayHint')}</p>}
        {refused && <p className="shape-hint warn">{t(refused)}</p>}
      </div>
    </>
  );
}
